import React, { useState, useEffect } from 'react';
import { Plus, Briefcase, Users, Shield, Crown, BookOpen, ShieldCheck, FileEdit } from 'lucide-react';
import RoleCard from './RoleCard';
import CreateRoleModal from './CreateRoleModal';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Template icons and colors for the quick-create section
const TEMPLATE_META = {
    employee: { icon: Users, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' },
    team_manager: { icon: Users, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
    admin: { icon: Shield, color: 'text-purple-600', bg: 'bg-purple-100', border: 'border-purple-200' },
    owner: { icon: Crown, color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-200' },
    onboarding_specialist: { icon: BookOpen, color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-200' },
    compliance_officer: { icon: ShieldCheck, color: 'text-rose-600', bg: 'bg-rose-100', border: 'border-rose-200' },
};

const RolesTab = () => {
    const [roles, setRoles] = useState([]);
    const [roleTemplates, setRoleTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null); // For editing
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await api.get('/employees/roles');
            setRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
        fetchRoleTemplates();
    }, []);

    const fetchRoleTemplates = async () => {
        try {
            const response = await api.get('/employees/roles/templates');
            setRoleTemplates(response.data.templates || []);
        } catch (error) {
            console.error('Error fetching role templates:', error);
        }
    };

    const handleCreateSuccess = () => {
        fetchRoles();
        toast.success(isEditing ? 'Role updated successfully! 🎉' : 'Role created successfully! 🎉');
        setShowCreateModal(false);
        setSelectedRole(null);
        setIsEditing(false);
    };

    const handleDeleteRole = async (role) => {
        if (!window.confirm(`Are you sure you want to delete the role "${role.role_name}"? \n\nAll existing employees with this role will be reverted to 'Basic Employee'.`)) {
            return;
        }

        try {
            await api.delete(`/employees/roles/${role.id}`);
            toast.success('Role deleted successfully');
            fetchRoles();
        } catch (error) {
            console.error('Error deleting role:', error);
            toast.error(error.response?.data?.error || 'Failed to delete role');
        }
    };

    const handleEditRole = (role) => {
        setSelectedRole(role);
        setIsEditing(true);
        setShowCreateModal(true);
    };

    const handleCreateClick = () => {
        setSelectedRole(null);
        setIsEditing(false);
        setSelectedTemplateId(null);
        setShowCreateModal(true);
    };

    const handleQuickCreateFromTemplate = (template) => {
        setSelectedRole({
            role_name: template.name,
            description: template.description || '',
            permissions: template.permissions || {},
        });
        setIsEditing(false);
        setSelectedTemplateId(template.id);
        setShowCreateModal(true);
    };

    const handleDuplicateRole = async (role) => {
        try {
            await api.post(`/employees/roles/${role.id}/duplicate`);
            toast.success(`Role '${role.role_name}' duplicated!`);
            fetchRoles();
        } catch (error) {
            console.error('Error duplicating role:', error);
            toast.error(error.response?.data?.error || 'Failed to duplicate role');
        }
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setSelectedRole(null);
        setIsEditing(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-foreground">Roles & Permissions</h2>
                    <p className="text-muted-foreground">Manage employee roles and access levels</p>
                </div>
                <button
                    onClick={handleCreateClick}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all flex items-center gap-2"
                >
                    <Plus size={18} />
                    Create Role
                </button>
            </div>

            {/* Quick Create Templates — Enhanced with icons, descriptions, and colors */}
            {roleTemplates.length > 0 && (
                <div className="bg-gradient-to-br from-muted/30 to-muted/10 border border-border rounded-xl p-4 md:p-5">
                    <p className="text-sm font-semibold text-foreground mb-1">Quick create from template</p>
                    <p className="text-xs text-muted-foreground mb-3">
                        Start with a pre-built role and customize as needed
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
                        {roleTemplates.map((template) => {
                            const meta = TEMPLATE_META[template.template_key] || { icon: Briefcase, color: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' };
                            const IconComponent = meta.icon;
                            const permCount = Object.keys(template.permissions || {}).length;

                            return (
                                <button
                                    key={template.id}
                                    onClick={() => handleQuickCreateFromTemplate(template)}
                                    className="group p-3 bg-card border border-border rounded-xl text-left hover:border-primary/40 hover:shadow-md transition-all"
                                >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${meta.bg} ${meta.border} border`}>
                                        <IconComponent size={16} className={meta.color} />
                                    </div>
                                    <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                        {template.name}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                        {permCount} permission{permCount !== 1 ? 's' : ''}
                                    </p>
                                </button>
                            );
                        })}
                        {/* Blank creation card */}
                        <button
                            onClick={handleCreateClick}
                            className="group p-3 bg-card border-2 border-dashed border-border rounded-xl text-left hover:border-primary/40 hover:shadow-md transition-all"
                        >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 bg-muted border border-border">
                                <FileEdit size={16} className="text-muted-foreground" />
                            </div>
                            <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                                Custom Role
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                                Start from scratch
                            </p>
                        </button>
                    </div>
                </div>
            )}

            {/* Existing Roles Grid */}
            {roles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {roles.map((role) => (
                        <RoleCard
                            key={role.id}
                            role={role}
                            employeeCount={role.employee_count || 0}
                            onEdit={handleEditRole}
                            onDelete={handleDeleteRole}
                            onDuplicate={handleDuplicateRole}
                            isSystemRole={role.is_system_role}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-muted/20 border border-dashed border-border rounded-xl">
                    <Briefcase size={40} className="mx-auto text-muted-foreground mb-3" />
                    <h3 className="text-lg font-semibold text-foreground mb-1">No roles created yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Create your first role from a template above, or start from scratch.
                    </p>
                    <button
                        onClick={handleCreateClick}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors"
                    >
                        Create Your First Role
                    </button>
                </div>
            )}

            {/* Reusing CreateRoleModal for both create and edit */}
            {showCreateModal && (
                <CreateRoleModal
                    isOpen={showCreateModal}
                    onClose={handleCloseModal}
                    onSuccess={handleCreateSuccess}
                    initialData={selectedRole}
                    isEditing={isEditing}
                />
            )}
        </div>
    );
};

export default RolesTab;
